<template>
  <v-dialog-wrapper
    :value="openDialog"
    max-width="674"
    persistent
    @input="value => !value && closeDialog()"
    @keydown.enter="confirmDialog"
  >
    <v-card-wrapper
      class="v-card"
    >
      <loader-component
        v-if="showLoader"
        :show="showLoader"
      />

      <v-card-title-wrapper
        class="v-card-title"
      >
        <div
          class="info-title"
        >
          {{ title }}
        </div>
        <v-spacer-wrapper />

        <div
          class="x-image"
          :class="{ 'x-image--disabled': showLoader }"
        >
          <v-img
            src="/img/icons/x-icon-image.svg"
            contain
            width="30"
            height="30"
            @click="closeDialog"
          />
        </div>
      </v-card-title-wrapper>

      <v-card-text-wrapper
        class="full-height pa-5 d-flex flex-column dialog-message"
      >
        <!-- eslint-disable vue/no-v-html -->
        <div v-html="dialogMessage" />
        <!-- eslint-enable vue/no-v-html -->
      </v-card-text-wrapper>

      <v-card-actions-wrapper>
        <v-row-wrapper
          style="margin: 0;"
        >
          <v-col-wrapper>
            <universal-button
              type="cancel"
              :label="negativeButtonText"
              :disabled="showLoader"
              class="universal-buttons"
              @click="closeDialog"
            />
          </v-col-wrapper>
          <v-col-wrapper>
            <universal-button
              type="save"
              :label="positiveButtonText"
              :color="positiveBtnColor"
              :disabled="showLoader"
              class="universal-buttons"
              @click="confirmDialog"
            />
          </v-col-wrapper>
        </v-row-wrapper>
      </v-card-actions-wrapper>
    </v-card-wrapper>
  </v-dialog-wrapper>
</template>

<script setup>
import { computed } from 'vue'
import { isFunction } from '@/global/utils/utils'
import UniversalButton from '@/global/components/buttons/UniversalButton.vue'
import LoaderComponent from '@/global/components/loader/Loader.vue'
import VDialogWrapper from '@/global/components/vuetifyComponentsWrappers/VDialogWrapper.vue'
import VCardWrapper from '@/global/components/vuetifyComponentsWrappers/VCardWrapper.vue'
import VCardTitleWrapper from '@/global/components/vuetifyComponentsWrappers/VCardTitleWrapper.vue'
import VCardTextWrapper from '@/global/components/vuetifyComponentsWrappers/VCardTextWrapper.vue'
import VCardActionsWrapper from '@/global/components/vuetifyComponentsWrappers/VCardActionsWrapper.vue'
import VRowWrapper from '@/global/components/vuetifyComponentsWrappers/VRowWrapper.vue'
import VColWrapper from '@/global/components/vuetifyComponentsWrappers/VColWrapper.vue'
import VSpacerWrapper from '@/global/components/vuetifyComponentsWrappers/VSpacerWrapper.vue'

const props = defineProps({
  dialogMessage: {
    type: String,
    default: ''
  },
  openDialog: {
    type: Boolean,
    default: false
  },
  positiveButtonText: {
    type: String,
    default: ''
  },
  positiveButtonConfig: {
    type: Object,
    default: () => ({
      color: '#65AD45'
    })
  },
  negativeButtonText: {
    type: String,
    default: ''
  },
  showLoader: {
    type: Boolean,
    default: false
  },
  confirmCallbackFunction: {
    type: Function,
    default: null
  },
  title: {
    type: String,
    default: ''
  }
})

const emit = defineEmits([
  'close',
  'close-inner'
])

const positiveBtnColor = computed(() => props.positiveButtonConfig?.color)

function closeDialog () {
  if (props.showLoader) {
    return
  }

  emit('close')
}

async function confirmDialog () {
  if (props.showLoader) {
    return
  }

  if (isFunction(props.confirmCallbackFunction)) {
    await props.confirmCallbackFunction()
  }
  emit('close-inner')
}
</script>

<style lang="scss" scoped>
.dialog-message {
  font-family: 'Satoshi', sans-serif;
  font-weight: 500 !important;
  font-size: 16px !important;
  line-height: 24px;
  color: #111827 !important;
}

.v-card {
  text-align: center;
  position: relative;
  border-radius: 16px !important;
  overflow: hidden;
}

.universal-buttons {
  width: 100%;
  height: 40px !important;
}

.v-card-title {
  font-family: 'Satoshi', sans-serif;
  font-weight: 500 !important;
  font-size: 18px !important;
  color: #111827 !important;
  border-bottom: 1px solid #E5E7EB;
}

.x-image:hover {
  cursor: pointer;
}

.x-image--disabled {
  opacity: 0.5;
  pointer-events: none;
}

.info-title {
  max-width: 60%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

::v-deep {
  .v-dialog {
    border-radius: 16px !important;
  }

  .loader-overlay .v-overlay__scrim {
    border-radius: 16px !important;
  }
}
</style>
